/** Error envelope the server returns for every /api/v1 failure. */
export type ApiErrorBody = {
  error: { code: string; message: string; fields?: Record<string, string>; details?: Record<string, unknown> };
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields?: Record<string, string>;
  /** Domain error details, e.g. `{ reason: "key_taken" }` (422) or `{ current }` (409 conflict). */
  readonly details?: Record<string, unknown>;

  constructor(
    status: number,
    code: string,
    message: string,
    fields?: Record<string, string>,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.details = details;
  }

  /** Stable machine-readable reason of a 422 validation error ("key_taken", "parent_level", ...). */
  get reason(): string | undefined {
    const r = this.details?.reason;
    return typeof r === "string" ? r : undefined;
  }
  get isConflict() {
    return this.status === 409;
  }

  get isUnauthorized() {
    return this.status === 401;
  }
  get isNetwork() {
    return this.status === 0;
  }
  /** 5xx, including the dev proxy answering 502 while the server restarts. */
  get isServer() {
    return this.status >= 500;
  }
}

type Options = { method?: string; body?: unknown; signal?: AbortSignal; headers?: Record<string, string> };

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const { method = "GET", body, signal, headers } = opts;
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: "same-origin",
      signal,
      headers: {
        Accept: "application/json",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    // Caller aborts propagate; a TimeoutError (guard timeout) is reported as a network failure.
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "network", "Network request failed");
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let json: unknown;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = undefined;
    }
  }

  if (!res.ok) {
    const env = (json as Partial<ApiErrorBody> | undefined)?.error;
    // Better Auth style bodies: { message, code }
    const flat = json as { message?: string; code?: string } | undefined;
    throw new ApiError(
      res.status,
      env?.code ?? flat?.code ?? (res.status >= 500 ? "server_error" : "request_failed"),
      env?.message ?? flat?.message ?? res.statusText,
      env?.fields,
      env?.details,
    );
  }
  return json as T;
}

api.get = <T>(path: string, signal?: AbortSignal) => api<T>(path, { signal });
api.post = <T>(path: string, body?: unknown) => api<T>(path, { method: "POST", body: body ?? {} });
api.patch = <T>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
api.put = <T>(path: string, body: unknown) => api<T>(path, { method: "PUT", body });
api.delete = <T>(path: string) => api<T>(path, { method: "DELETE" });
